import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router";
import { Search } from "@iscr/xAppLib/UIelems/Search";
import { Dialog } from "@headlessui/react";
import { validate } from "uuid";
import menu_model from "@iscr/models/menu_model";
import { useDebouncedValue, useUser } from "@iscr/xAppLib/Hooks";
import user_model from "@iscr/models/user_model";
import { toDictionary } from "@iscr/xAppLib/helpers/toDictionary";
import SIcon from "../NUI/StreamlineIcons/SIcon";
import { clsx } from "clsx";
import { useMediaQuery } from "usehooks-ts";
function escapeRegExp(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
function initials(name) {
    return name
        .split(" ")
        .map(x => x[0])
        .join("");
}
const matchesUR = (s) => /^UR_\w{10}$/.test(s);
const matchesIHI = (s) => /^\d{16}$/.test(s);
const matchesSnum = (s) => /^\d+$/.test(s);
const matchesGuid = (s) => validate(s) || (s.endsWith("+") && validate(s.slice(0, s.length - 1)));
const matchesEmail = (s) => /^[^ ]+@[^ ]+$/.test(s);
export function SpotlightTrigger() {
    return (_jsx("button", { type: "button", onClick: () => app.trigger(app.events.SPOTLIGHT), children: _jsx(SIcon, { name: "search", size: "xs", "aria-hidden": "true" }) }));
}
export function Spotlight() {
    const navigate = useNavigate();
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState("");
    const user = useUser();
    const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
    useEffect(() => {
        const toggle = () => {
            setOpen(o => !o);
            setSearch("");
        };
        const onKeyDown = (e) => {
            if (e.key === "/" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                toggle();
            }
        };
        app.on(app.events.SPOTLIGHT, toggle);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("keydown", onKeyDown);
            app.off(app.events.SPOTLIGHT, toggle);
        };
    }, []);
    const menu = useMemo(() => {
        const topMenu = menu_model.get_menu(false).filter(Boolean);
        // @ts-ignore
        const userMenu = Object.keys(user_model.ROLE_LIST)
            // @ts-ignore
            .sort((a, b) => b - a)
            // @ts-ignore
            .map(r => 
        // @ts-ignore
        app.user.claims[user_model.ROLE_LIST[r].c] &&
            // @ts-ignore
            app.app_data.ws_conf.user_menus["menu_" + user_model.ROLE_LIST[r].c])
            .filter(Boolean)
            .flat()
            .filter((item) => !item.role || user_model.check_access(item.role))
            .reduce((acc, item) => {
            var _a;
            const current = acc[acc.length - 1];
            if (item.type === "header") {
                acc.push({ menu: ["User Menu", item.name], items: [] });
                return acc;
            }
            if (item.type === "subheader") {
                const parentMenu = (current === null || current === void 0 ? void 0 : current.items.length) > 0 ? current.menu.slice(0, 2) : ((_a = current === null || current === void 0 ? void 0 : current.menu) !== null && _a !== void 0 ? _a : []);
                acc.push({ menu: [...parentMenu, item.name], items: [] });
                return acc;
            }
            if (item.name && item.u) {
                current === null || current === void 0 ? void 0 : current.items.push({ name: item.name, link: item.u, icon: item.ico });
            }
            return acc;
        }, []);
        const nested = topMenu.concat(userMenu);
        return nested.flatMap(group => {
            var _a;
            return ((_a = group.items) !== null && _a !== void 0 ? _a : [])
                .filter((x) => typeof x === "object" && x !== null && x.name)
                .map((item) => (Object.assign(Object.assign({}, item), { group: Array.isArray(group.menu) ? group.menu.join(" > ") : group.menu })));
        });
    }, [user]);
    const results = useMemo(() => {
        const trimmed = search.trim();
        if (!trimmed)
            return [];
        const re = new RegExp(escapeRegExp(trimmed), "i");
        const words = trimmed.split(/\s+/).map(word => new RegExp(escapeRegExp(word), "i"));
        const menuResults = menu
            .filter(item => {
            var _a;
            return re.test(item.name) ||
                re.test((_a = item.group) !== null && _a !== void 0 ? _a : "") ||
                re.test(item.link) ||
                re.test(initials(item.name)) ||
                words.every(w => w.test(item.name)) ||
                words.every(w => { var _a; return w.test((_a = item.group) !== null && _a !== void 0 ? _a : ""); });
        })
            .map(item => ({ title: item.name, link: item.link, description: item.group }))
            .slice(0, 10);
        const contextual = [];
        const isUserListLike = user_model.limit_access("user_admin") &&
            trimmed.split(/\s+/).every(word => matchesUR(word) || matchesEmail(word) || matchesIHI(word));
        if (isUserListLike)
            contextual.push({
                title: "User admin",
                link: "/admin/users",
                description: "Search for users",
                state: { srch_str: trimmed },
            });
        const isScriptListLike = user_model.limit_access("view_scripts") &&
            trimmed
                .split(/\s+/)
                .every(word => matchesUR(word) ||
                matchesSnum(word) ||
                matchesGuid(word) ||
                matchesEmail(word) ||
                matchesIHI(word));
        if (isScriptListLike)
            contextual.push({
                title: "Scripts list",
                link: "/scripts/list/support",
                description: "Search for scripts",
                state: { srch: trimmed },
            });
        const categories = [
            { name: "Menu", results: menuResults },
            { name: "Search", results: contextual },
        ]
            .filter(cat => cat.results.length > 0)
            .map(cat => (Object.assign(Object.assign({}, cat), { results: cat.results.map((result, i) => (Object.assign(Object.assign({}, result), { key: `${cat.name}-${result.link}-${i}` }))) })));
        if (categories.length === 0)
            return [];
        if (categories.length === 1)
            return categories[0].results;
        return toDictionary(categories, cat => cat.name);
    }, [search, menu]);
    const delayedClose = useDebouncedValue(open, open || reducedMotion ? 0 : 100);
    return (_jsxs(Dialog, { open: delayedClose, onClose: () => setOpen(false), className: "fullscreen relative z-[1000]", children: [_jsx("div", { className: clsx("fixed inset-0", !reducedMotion && "duration-100 animate-fade", open ? "backdrop-blur-sm  bg-black/90" : "backdrop-blur-0 bg-black/0") }), open && (_jsx("div", { className: "flex justify-center fixed inset-0 p-4", children: _jsx(Dialog.Panel, { className: "w-full max-w-[400px] p-6", children: _jsx(Search, { autoFocus: true, fluid: true, input: search, placeholder: "Search", onResultSelect: result => {
                            result.link && navigate(result.link, { state: result.state });
                            setOpen(false);
                        }, onSearchChange: d => setSearch(d !== null && d !== void 0 ? d : ""), category: !Array.isArray(results), results: results }) }) }))] }));
}
